import { DotLegalDialog, DotLegalHeader, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { Box, LinearProgress, TableCell, TableRow } from "@mui/material";
import { useLegalEntityTypeNames } from "../../../legalEntity/useLegalEntityTypeNames.ts";
import { LegalEntityAuditGroupSendModel } from "./SendAuditsDialog.types";
import useSendAuditsDialogHooks from "./SendAuditsDialog.hooks";

export interface ISendAuditsDialog {
    selectedEntities: Array<LegalEntityAuditGroupSendModel>;
    onDialogClose: () => void;
    auditGroupId?: string;
    auditGroupName: string;
}

function SendAuditsDialog(props: ISendAuditsDialog) {
    const { translateString, translateCountry } = useTranslation();
    const { getLegalEntityTypeName } = useLegalEntityTypeNames();
    const { send, uploadState, isSending, uploadCounter } = useSendAuditsDialogHooks(props);

    function getHeaders() {
        let headers = Array<ITableHeader<LegalEntityAuditGroupSendModel>>();
        headers.push({ property: "legalEntityName", text: translateString("legalEntity"), align: "left", showOnMobile: true });
        headers.push({ property: "legalEntityTypes", text: translateString("type"), align: "left", showOnMobile: true });
        headers.push({ property: "countryCode", text: translateString("country"), align: "left", showOnMobile: true });
        headers.push({ property: "contactName", text: translateString("contact"), align: "left", showOnMobile: true });
        headers.push({ property: "contactEmail", text: translateString("contactEmail"), align: "left", showOnMobile: true });
        headers.push({ property: "additionalRecipientNames", text: translateString("additionalRecipients"), align: "left", showOnMobile: true });
        headers.push({ property: "systemNames", text: translateString("assets"), align: "left", showOnMobile: true });

        return headers;
    }

    const formatLegalEntityTypes = (legalEntityTypes: Array<string>) => {
        return legalEntityTypes.map((x) => getLegalEntityTypeName(x)).join(", ");
    };

    return (
        <DotLegalDialog
            buttonOkText={`${translateString("Send")} (${props.selectedEntities.length})`}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={() => send()}
            size="xl"
            buttonRight
            header={translateString("sendAudits")}
            inProgress={isSending}
        >
            <DotLegalTable
                headers={getHeaders()}
                defaultOrderBy={"legalEntityName"}
                defaultOrder={"asc"}
                isLoading={false}
                paginationLabelOf={translateString("labelOf")}
                data={props.selectedEntities}
                clickableRows={false}
                renderRow={(row) => {
                    return (
                        <TableRow key={row.legalEntityId}>
                            <TableCell>{row.legalEntityName}</TableCell>
                            <TableCell>{formatLegalEntityTypes(row.legalEntityTypes)}</TableCell>
                            <TableCell>{translateCountry(row.countryCode)}</TableCell>
                            <TableCell>{row.contactName}</TableCell>
                            <TableCell>{row.contactEmail}</TableCell>
                            <TableCell>{row.additionalRecipientNames.join(", ")}</TableCell>
                            <TableCell>{row.systemNames.join(", ")}</TableCell>
                        </TableRow>
                    );
                }}
            />

            <Box sx={{ height: 20, visibility: isSending ? "visible" : "hidden" }} marginTop={2} marginBottom={2}>
                <DotLegalHeader headerStyle={"small"} center>
                    {`Sender ${uploadCounter} af ${props.selectedEntities.length}`}
                </DotLegalHeader>
                <LinearProgress sx={{ height: 10, borderRadius: 5 }} variant="determinate" value={uploadState} />
            </Box>
        </DotLegalDialog>
    );
}

export default SendAuditsDialog;
